import { deepEquals, exists } from '@jack-henry/frontend-utils/functions';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TmBaseComponent } from '../tm-base.component';
import { SelectItemVm, TmValueChanged } from './forms';
import './tm-select';
import './tm-text-field';

export enum TextRangeType {
    All = 'All',
    Specific = 'Specific',
    Range = 'Range',
    Between = 'Between',
    LessThan = 'Less Than',
    GreaterThan = 'Greater Than',
}

export interface TextRange {
    rangeType: TextRangeType;
    specificText?: string;
    floorText?: string;
    ceilText?: string;
}

export const tagName = 'tm-text-range';
@customElement(tagName)
export class TmTextRange extends TmBaseComponent {
    @property({ type: String })
    public label = '';

    @property({ type: Array })
    public textRangeOptions: TextRangeType[] = [
        TextRangeType.All,
        TextRangeType.Specific,
        TextRangeType.Range,
    ];

    @property({ type: Object })
    public range: TextRange = {
        rangeType: TextRangeType.All,
    };

    @state()
    private rangeType?: TextRangeType;

    @state()
    private specificText?: string;

    @state()
    private floorText?: string;

    @state()
    private ceilText?: string;

    private get rangeOptionsForSelect(): SelectItemVm<string>[] {
        return this.textRangeOptions.map(rangeOption => ({
            label: rangeOption,
            value: rangeOption,
        }));
    }

    private get isValidTextRange() {
        if (!this.rangeType) {
            return false;
        }
        switch (this.rangeType) {
            case TextRangeType.Specific:
                return exists(this.specificText) && this.specificText.length > 0;
            case TextRangeType.Range:
            case TextRangeType.Between: {
                if (exists(this.floorText) && exists(this.ceilText)) {
                    return this.floorText <= this.ceilText && this.ceilText.length > 0;
                }

                return true;
            }
            default:
                return true;
        }
    }

    protected updated(changedProperties: Map<PropertyKey, unknown>): void {
        if (
            changedProperties.has('range') &&
            this.range &&
            (!deepEquals(changedProperties.get('range'), this.range) || !this.isValidTextRange)
        ) {
            this.parseRange();
        }
    }

    protected render() {
        return [
            this.renderRangeTypeSelect(),
            this.renderSpecificAmountField(),
            this.renderRangedAmountFields(),
        ];
    }

    private parseRange() {
        const { rangeType, specificText, floorText, ceilText } = this.range;
        this.rangeType = rangeType;
        this.specificText = specificText;
        this.floorText = floorText;
        this.ceilText = ceilText;
    }

    private dispatchSelectionEvent() {
        let detail: TextRange | false = false;
        if (this.isValidTextRange) {
            const { rangeType, specificText, floorText, ceilText } = this;
            detail = {
                rangeType,
                ...(specificText && { specificText }),
                ...(exists(floorText) && { floorText }),
                ...(ceilText && { ceilText }),
            } as TextRange;
        }
        this.dispatchEvent(new CustomEvent('selection', { detail }));
    }

    private updateAmounts() {
        if (!this.rangeType) {
            return;
        }

        this.specificText = undefined;
        this.floorText = undefined;
        this.ceilText = undefined;

        switch (this.rangeType as TextRangeType) {
            case TextRangeType.Specific:
            case TextRangeType.LessThan:
            case TextRangeType.GreaterThan:
                this.specificText = '';
                break;
            case TextRangeType.Range:
            case TextRangeType.Between:
                this.floorText = '';
                this.ceilText = '';
                break;
            default:
                break;
        }
    }

    private renderRangeTypeSelect() {
        return html` <tm-select
            .label=${this.label}
            .items=${this.rangeOptionsForSelect}
            .initial=${this.rangeType}
            @value-changed=${(e: TmValueChanged<TextRangeType>) => {
                if (e.detail.value) {
                    this.rangeType = e.detail.value;
                    this.updateAmounts();
                    this.dispatchSelectionEvent();
                }
            }}
        ></tm-select>`;
    }

    private renderSpecificAmountField() {
        const showSpecificAmountField =
            this.rangeType === TextRangeType.Specific ||
            this.rangeType === TextRangeType.LessThan ||
            this.rangeType === TextRangeType.GreaterThan;
        if (!showSpecificAmountField) {
            return nothing;
        }
        return html` <tm-text-field
            id="specific-text"
            .initial=${this.specificText ? this.specificText?.toString() : ''}
            @value-changed=${(e: CustomEvent) => {
                this.specificText = e.detail.value;
                this.dispatchSelectionEvent();
            }}
        ></tm-text-field>`;
    }

    private renderFloorAmountField() {
        return html` <tm-text-field
            .initial=${this.floorText ? this.floorText?.toString() : ''}
            @value-changed=${(e: CustomEvent) => {
                this.floorText = e.detail.value;
                this.dispatchSelectionEvent();
            }}
            .invalid=${!this.isValidTextRange &&
            exists(this.floorText) &&
            this.floorText.length > 0}
            class="min-w-0 w-full"
        ></tm-text-field>`;
    }

    private renderCeilAmountField() {
        return html` <tm-text-field
            .initial=${this.ceilText ? this.ceilText?.toString() : ''}
            @value-changed=${(e: CustomEvent) => {
                this.ceilText = e.detail.value;
                this.dispatchSelectionEvent();
            }}
            .invalid=${!this.isValidTextRange &&
            exists(this.floorText) &&
            this.floorText.length > 0}
            class="min-w-0 w-full"
        ></tm-text-field>`;
    }

    private renderRangedAmountFields() {
        const showRangeFields =
            this.rangeType === TextRangeType.Range || this.rangeType === TextRangeType.Between;
        if (!showRangeFields) {
            return nothing;
        }
        return html` <div class="flex items-center">
            ${this.renderFloorAmountField()}
            <div class="px-2">-</div>
            ${this.renderCeilAmountField()}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTextRange;
    }
}
